import { anySignal } from "@-/com.npmjs.any-signal";
import {
  boundedPromise,
  DeadlineExceededError,
  notifyDeadlineExceeded,
} from "@-/dev.improve.haniverse.lib.deadline";

/**
 * Await `run` with a deadline that RESTARTS whenever it calls `progress()`, under a hard ceiling.
 *
 * For legitimately long work whose length depends on data size (a first sync of a large deck): a
 * fixed deadline either cuts a healthy transfer off or is so long that a dead connection spins for
 * minutes. Silence for `idleDeadlineMs` is the real symptom of a hang; the ceiling only stops a
 * progress loop that never finishes. Either expiry rejects with a `DeadlineExceededError` (reported
 * through `onDeadlineExceeded`) and aborts `signal`.
 */
export function runWithProgressDeadline<T>(
  {
    ceilingMs,
    idleDeadlineMs,
    operation,
  }: { ceilingMs: number; idleDeadlineMs: number; operation: string },
  run: (progress: () => void, signal: AbortSignal) => Promise<T>,
): Promise<T> {
  return boundedPromise<T>({ deadline: ceilingMs, operation }, (resolve, reject, ceiling) => {
    const idle = new AbortController();
    const signal = anySignal([ceiling, idle.signal]);
    let timer: ReturnType<typeof setTimeout> | undefined;
    let finished = false;
    const progress = () => {
      clearTimeout(timer);
      if (finished) {
        // A straggler reporting after the wait ended must not re-arm a timer.
        return;
      }
      timer = setTimeout(() => {
        const error = new DeadlineExceededError(`${operation} (no progress)`, idleDeadlineMs);
        notifyDeadlineExceeded(error);
        idle.abort(error);
        reject(error);
      }, idleDeadlineMs);
    };
    progress();
    run(progress, signal).then(resolve, reject);
    return () => {
      finished = true;
      clearTimeout(timer);
    };
  });
}
